/**
 * Minimum-notice filter: drop slots that start before `now + N hours`.
 *
 * Companies often say "interviews must be booked at least 2 hours
 * ahead". This filter trims those slots so users never see them.
 *
 * If a slot *partially* overlaps the notice window (starts before it,
 * ends inside it), we trim the slot's start forward. If trimming makes
 * it shorter than `minSlotMinutes`, we drop it entirely.
 */
import moment from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import type { AvailableSlot } from './types';

const MS_PER_MINUTE = 60_000;

export interface ApplyMinimumNoticeArgs {
  slots: AvailableSlot[];
  noticeHours: number;
  timezone: string;
  minSlotMinutes?: number;
}

export function applyMinimumNotice({
  slots,
  noticeHours,
  timezone,
  minSlotMinutes = 15,
}: ApplyMinimumNoticeArgs): AvailableSlot[] {
  if (noticeHours <= 0 || slots.length === 0) {
    return slots;
  }
  const cutoff = moment().tz(timezone).add(noticeHours, 'hours');
  const minMs = minSlotMinutes * MS_PER_MINUTE;
  const kept: AvailableSlot[] = [];
  for (const slot of slots) {
    const start = moment.tz(slot.start, timezone);
    const end = moment.tz(slot.end, timezone);
    if (end.isSameOrBefore(cutoff)) {
      continue;
    }
    if (start.isSameOrAfter(cutoff)) {
      kept.push(slot);
      continue;
    }
    // Partial overlap — trim.
    if (end.diff(cutoff) < minMs) {
      continue;
    }
    kept.push({
      ...slot,
      id: uuid(),
      start: cutoff.toISOString(),
    });
  }
  return kept;
}

/**
 * Generate background "disabled" intervals covering the minimum-notice
 * window. Useful for shading the calendar.
 */
export function buildMinimumNoticeBlockIntervals({
  noticeHours,
  timezone,
  windowStart,
  windowEnd,
}: {
  noticeHours: number;
  timezone: string;
  /** Start of the visible calendar window (ISO). */
  windowStart: string;
  /** End of the visible calendar window (ISO). */
  windowEnd: string;
}): { start: string; end: string }[] {
  if (noticeHours <= 0) {
    return [];
  }
  const cutoff = moment().tz(timezone).add(noticeHours, 'hours');
  const start = moment.tz(windowStart, timezone);
  const end = moment.min(moment.tz(windowEnd, timezone), cutoff);
  if (end.isSameOrBefore(start)) {
    return [];
  }
  return [{ start: start.toISOString(), end: end.toISOString() }];
}
